import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Button,
  TextField,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
const size = {
  xs: 16,
  md: 18,
  lg: 20,
};
const fontSizes = {
  xs: 8,
  md: 12,
  lg: 14,
};

const fontSizes1 = {
  xs: 8,
  md: 10,
  lg: 12,
};
const scrollbarStyles = {
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": {
    background: "#DFDFDF",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#DFDFDF",
    borderRadius: "10px",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#DFDFDF #DFDFDF",
};
export default function MultiSelectDropdown({
  label,
  options,
  onChange,
  formValues,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSelected, setTempSelected] = useState([]);
  const anchorRef = useRef(null);

  // Sync tempSelected when popover opens or formValues change
  useEffect(() => {
    setTempSelected(formValues || []);
  }, [formValues, open]);

  const filteredOptions = useMemo(
    () =>
      (options || []).filter((opt) =>
        opt.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  const handleToggle = (name) => {
    setTempSelected((prev) =>
      prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name],
    );
  };

  const handleApply = () => {
    onChange(tempSelected);
    setOpen(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    setTempSelected([]);
    onChange([]);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  const selectedCount = formValues?.length || 0;

  return (
    <Box sx={{ position: "relative", minWidth: 142, flex: "0 0 auto" }}>
      {/* Label */}
      {label && (
        <Typography
          sx={{
            fontSize: fontSizes1,
            fontWeight: 500,
            color: "#667F93",
            mb: "8px",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {label}
        </Typography>
      )}

      {/* Trigger Button */}
      <Box
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "4px",
          height: 42,
          px: "8px",
          border: "1px solid #D0D5DD",
          borderRadius: "10px",
          backgroundColor: "#fff",
          cursor: "pointer",
          transition: "border-color 0.2s",
          "&:hover": { borderColor: "#0066CC" },
          ...(open && { borderColor: "#0066CC" }),
        }}
      >
        <Typography
          sx={{
            fontSize: fontSizes1,
            color: selectedCount > 0 ? "#1A2025" : "#98A2B3",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
            textAlign: "start",
          }}
        >
          {selectedCount > 0
            ? formValues[0].length > 10
              ? formValues[0].slice(0, 10) + "..."
              : formValues[0]
            : "Choose"}
        </Typography>
        {selectedCount > 1 && (
          <Box
            sx={{
              backgroundColor: "#EAF2FF",
              color: "#0066CC",
              fontSize: fontSizes1,
              fontWeight: 600,
              borderRadius: "6px",
              padding: "2px 8px",
              whiteSpace: "nowrap",
              flexShrink: 0,
              minWidth: "20px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50px",
              padding: "10px",
            }}
          >
            {selectedCount}
          </Box>
        )}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#667F93",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {/* Dropdown Popover */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={4}
            sx={{
              width: 280,
              maxHeight: 420,
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              background:
                "linear-gradient(180deg, #C2E0FF -119.85%, #FFFFFF 100%)",
              boxShadow: "0px 0px 20px 0px #0000001F",
              ...scrollbarStyles,
            }}
          >
            {/* Search */}
            <Box sx={{ p: "14px 14px 10px" }}>
              <TextField
                placeholder="Search"
                size="small"
                fullWidth
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          fontSize: {
                            xs: 12,
                            md: 14,
                            lg: 18,
                          },
                          color: "#98A2B3",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    fontSize: fontSizes,
                    borderRadius: "10px",
                    backgroundColor: "transparent",
                    "& fieldset": {
                      borderColor: "#D0D5DD",
                    },
                    "&:hover fieldset": {
                      borderColor: "#0066CC",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0066CC",
                      borderWidth: "1.5px",
                    },
                  },
                }}
              />
            </Box>

            {/* Options List */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: "4px",
                maxHeight: 280,
              }}
            >
              {filteredOptions.map((name) => {
                const isChecked = tempSelected.includes(name);
                return (
                  <Box
                    key={name}
                    onClick={() => handleToggle(name)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { sm: "6px", md: "8px", lg: "10px" },
                      px: {
                        sm: "8px",
                        md: "10px",
                        lg: "12px",
                      },
                      py: {
                        sm: "4px",
                        md: "6px",
                        lg: "8px",
                      },
                      borderRadius: "6px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#F5F7FA" },
                    }}
                  >
                    {/* <Checkbox
                      size="small"
                      checked={isChecked}
                      sx={{
                        p: 0,
                        color: "#D0D5DD",
                        "&.Mui-checked": { color: "#0066CC" },
                        "& .MuiSvgIcon-root": { fontSize: 20 },
                      }}
                    /> */}
                    <Checkbox
                      checked={isChecked}
                      disableRipple
                      icon={
                        <Box
                          sx={{
                            width: size,
                            height: size,
                            border: "1px solid #B3C5D4",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                          }}
                        />
                      }
                      checkedIcon={
                        <Box
                          sx={{
                            width: size,
                            height: size,
                            border: "1px solid #354ACD",
                            borderRadius: "6px",
                            backgroundColor: "#354ACD",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CheckIcon
                            sx={{
                              fontSize: {
                                xs: 10,
                                md: 12,
                                lg: 16,
                              },
                              color: "#fff",
                            }}
                          />
                        </Box>
                      }
                      sx={{
                        p: 0,
                        mr: 1,
                        "&:hover .MuiBox-root": {
                          borderColor: "#354ACD",
                          backgroundColor: "#EEF3FF",
                        },

                        "&.Mui-checked:hover .MuiBox-root": {
                          backgroundColor: "#354ACD",
                        },

                        "&.Mui-disabled:hover .MuiBox-root": {
                          borderColor: "#D9E1E7",
                          backgroundColor: "#F5F7FA",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: fontSizes,
                        color: "#344054",
                      }}
                    >
                      {name}
                    </Typography>
                  </Box>
                );
              })}

              {filteredOptions.length === 0 && (
                <Typography
                  sx={{
                    fontSize: fontSizes1,
                    color: "#98A2B3",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  No results found
                </Typography>
              )}
            </Box>

            {/* Footer: Clear / Apply */}
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                p: "14px 16px",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClear}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontSize: fontSizes,
                  fontWeight: 500,
                  borderRadius: "24px",
                  height: 40,
                  color: "#344054",
                  borderColor: "#D0D5DD",
                  "&:hover": {
                    borderColor: "#98A2B3",
                    backgroundColor: "#F9FAFB",
                  },
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontSize: fontSizes,
                  fontWeight: 500,
                  borderRadius: "24px",
                  height: 40,
                  backgroundColor: "#0066CC",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#0052A3",
                    boxShadow: "none",
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
